import { Box, DialogContentText, Paper, Stack, Typography, FormHelperText, Chip, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Autocomplete,
  Button,
  CheckBox,
  Datepicker,
  GridContainer,
  GridItem,
  Select,
  TextField,
  Modal,
  FieldWarning,
  FieldLabel,
  ErrorModal,
  InactiveAreaTaxModal,
} from '../../../components/shared';
import { TextMaskCustom } from '../../../components/TextMaskCustom/TextMaskCustom';
import { subLocAddUpdateSchema } from '../../../schema/validationSchemas';
import {
  useGetAllCountriesQuery,
  useGetCountryStatesByCountryIdQuery,
  useGetStateCitiesByStateIdQuery,
} from '../../../services/CountriesAndStatesAndCities/CountriesAndStatesAndCities';
import {
  checkValueNotNullUndefinedBlank,
  formatPhNo10Digit,
  getOptions,
  getTimeFormatForFormView,
  getDisplayTime,
  dateFormatForApi,
  getPhNo10Digits,
  get24HoursTimeFormat,
} from '../../../utils/common-methods';
import { LoadingButton } from '@mui/lab';
import { useState, useEffect, useMemo } from 'react';
import {
  useUpdateSubLocationByIdMutation,
  useGetSubLocationTypesQuery,
  useGetProductCategoriesQuery,
  useGetAreaTaxLocationsQuery,
} from '../../../services/Sublocation/SublocationService';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { routeConstants } from '../../../constants/routeConstants';
import ConfirmationModal from '../../../components/ConfirmationModel/ConfirmationModal';
import {
  useDebounce,
  useSubLocationStatusValidation,
  useSubLocationSaveValidation,
  usePostingLocationSync,
  getPostingLocationForSubmission,
  getPostingLocationValue,
  isArcadeOrGameRoomType,
} from '../../../hooks';
import { PercentTextField } from '../../../components/shared';
import { MESSAGE } from '../../../constants/message';
import { isSelectedAreaTaxLocationInactive } from '../../../utils/sublocationValidations';
import { FEED_SOURCE_OPTIONS, getCreditCashOptions } from '../../../constants/locationConstants';
import { parseFeedSourceValues } from '../../../utils/common-methods';
import { formatTaxPercentValue } from '../../../utils/common-methods';
import { ASSET_STATUS } from '../../../constants/assetStatus';

const SubLocationUpdateView = (props) => {
  const [areaTaxLocationSearchText, setAreaTaxLocationSearchText] = useState('');
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });
  const { subLocationDetails } = props;
  const [isActiveAssets, setIsActiveAssets] = useState(false);
  const { currentData: locationTypesData } = useGetSubLocationTypesQuery();
  const { currentData: countryData } = useGetAllCountriesQuery();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get('locationId');
  const navigate = useNavigate();
  const [creditCashControlValue, setCreditCashControlValue] = useState(null);
  const debouncedTaxLocationSearchText = useDebounce(areaTaxLocationSearchText, 300);
  const openErrorModal = (message) => setErrorModal({ open: true, message });
  const closeErrorModal = () => setErrorModal({ open: false, message: '' });

  const { currentData: statesData } = useGetCountryStatesByCountryIdQuery(subLocationDetails?.location?.countryId, {
    skip: !subLocationDetails?.location?.countryId,
  });
  const { currentData: citiesData } = useGetStateCitiesByStateIdQuery(subLocationDetails?.location?.stateId, {
    skip: !subLocationDetails?.location?.stateId,
  });

  const [updateSubLocationApi, { isLoading: updating }] = useUpdateSubLocationByIdMutation();
  const locationTypes = getOptions(locationTypesData);
  const countryOptions = getOptions(countryData);
  const stateOptions = getOptions(statesData?.data?.states);
  const citiesOptions = getOptions(citiesData?.data?.cities);
  const { currentData: productTypes } = useGetProductCategoriesQuery();

  const { currentData: areaTaxLocations } = useGetAreaTaxLocationsQuery({
    page: 1,
    perPage: 100,
    searchParams: `q=${debouncedTaxLocationSearchText}`,
  });

  const leaseCCFeeRate =
    subLocationDetails?.leaseCreditCardFeeRate != null ? Number(subLocationDetails.leaseCreditCardFeeRate) : undefined;

  const productTypeOptions = getOptions(productTypes);
  const productTypesInitialValues = subLocationDetails?.productCategories?.map((product) => ({
    text: product?.name,
    value: product?.id,
    id: product?.id,
    name: product?.name,
  }));

  const taxExemptOptions = [
    { value: true, text: 'Yes' },
    { value: false, text: 'No' },
  ];

  useEffect(() => {
    const assets = Array.isArray(subLocationDetails?.assets) ? subLocationDetails.assets : [];
    const hasActiveAsset = assets.some((asset) => asset.statusId === ASSET_STATUS.ACTIVE);
    setIsActiveAssets(hasActiveAsset);

    if (subLocationDetails?.creditCashControl) {
      const validOptions = getCreditCashOptions(subLocationDetails?.calculateRent || false);
      const initialValue = validOptions.find((option) => option.value === subLocationDetails.creditCashControl);
      setCreditCashControlValue(initialValue || null);
    } else {
      setCreditCashControlValue(null);
    }
  }, []);

  // Use custom hooks for status validation
  const {
    onStatusToggleClick: onStatusToggleClickHandler,
    showInactiveAreaTaxModal,
    setShowInactiveAreaTaxModal,
    showWarningModal,
    setShowWarningModal,
    showArchiveModal,
    setShowArchiveModal,
  } = useSubLocationStatusValidation(subLocationDetails, isActiveAssets);

  // Use custom hook for save validation (shares modal state with archive hook)
  const { validateBeforeSave } = useSubLocationSaveValidation(
    subLocationDetails,
    areaTaxLocations,
    setShowInactiveAreaTaxModal,
  );

  const formik = useFormik({
    initialValues: {
      name: subLocationDetails?.name || '',
      zipCode: subLocationDetails?.code || '',
      phone1: formatPhNo10Digit(subLocationDetails?.phone1) || '',
      phone2: formatPhNo10Digit(subLocationDetails?.phone2) || '',
      openHours: subLocationDetails?.openHours ? getTimeFormatForFormView(subLocationDetails.openHours) : null,
      closeHours: subLocationDetails?.closeHours ? getTimeFormatForFormView(subLocationDetails.closeHours) : null,
      type: subLocationDetails?.type || '',
      postingLocation: getPostingLocationValue(
        {
          postingLocation: subLocationDetails?.postingLocation,
          type: subLocationDetails?.type,
          name: subLocationDetails?.name,
        },
        locationTypesData,
      ),
      location: subLocationDetails?.location?.locationName || '',
      country: subLocationDetails?.location?.countryId || '',
      countryRegion: subLocationDetails?.locationCountryRegion || '',
      state: subLocationDetails?.location?.stateId || '',
      stateTimezone: subLocationDetails?.locationStateTimezone || '',
      city: subLocationDetails?.location?.cityId || '',
      startDate:
        checkValueNotNullUndefinedBlank(subLocationDetails?.startDate) &&
        dayjs(subLocationDetails?.startDate).toString() !== 'Invalid Date'
          ? dayjs(subLocationDetails?.startDate).toString()
          : null,
      endDate:
        checkValueNotNullUndefinedBlank(subLocationDetails?.endDate) &&
        dayjs(subLocationDetails?.endDate).toString() !== 'Invalid Date'
          ? dayjs(subLocationDetails?.endDate).toString()
          : null,
      uninstallDate:
        checkValueNotNullUndefinedBlank(subLocationDetails?.uninstallDate) &&
        dayjs(subLocationDetails?.uninstallDate).toString() !== 'Invalid Date'
          ? dayjs(subLocationDetails?.uninstallDate).toString()
          : null,
      spaceIdentifier: subLocationDetails?.spaceIdentifier || '',
      address1: subLocationDetails?.address1 || '',
      address2: subLocationDetails?.address2 || '',
      productType: productTypesInitialValues?.[0] || null,
      vendorCode: subLocationDetails?.vendor?.vendorCode || 'N/A',
      vendorName: subLocationDetails?.vendor?.customerName || 'N/A',
      areaTaxLocation: {
        id: subLocationDetails?.areaTaxLocations?.id,
        text: subLocationDetails?.areaTaxLocations?.areaTaxLocationName,
        value: subLocationDetails?.areaTaxLocations?.areaTaxLocationName || '',
      },
      areaTaxLocationId: subLocationDetails?.areaTaxLocations?.id || null,
      stateTaxRate: subLocationDetails?.location?.state?.stateTaxHistories?.[0]?.stateTax || 'N/A',
      areaTaxRate: subLocationDetails?.areaTaxHistories?.[0]?.areaTax || 'N/A',
      miscTax: subLocationDetails?.miscTax || 0,
      ccFeePercent: subLocationDetails?.creditCardFeeRate ?? leaseCCFeeRate ?? '',
      pennyCostPercentage: subLocationDetails?.pennyCostPercentage || 0,
      locPaysTaxFlag:
        subLocationDetails?.areaTaxHistories?.[0]?.locPaysTaxFlag ?? subLocationDetails?.locPaysTaxFlag ?? false,
      calculateRent: subLocationDetails?.calculateRent || false,
      taxExemptFlag:
        subLocationDetails?.areaTaxHistories?.[0]?.taxExemptFlag ?? subLocationDetails?.taxExemptFlag ?? false,
      isCreditCardInCollection: subLocationDetails?.isCreditCardInCollection,
      feedSource: parseFeedSourceValues(subLocationDetails?.feedSource) || [],
      creditCashControl: (() => {
        if (subLocationDetails?.creditCashControl) {
          const validOptions = getCreditCashOptions(subLocationDetails?.calculateRent || false);
          return validOptions.find((option) => option.value === subLocationDetails.creditCashControl) || null;
        }
        return null;
      })(),
    },
    validationSchema: subLocAddUpdateSchema,
    onSubmit: (values) => {
      // Validate area tax location before saving
      if (!validateBeforeSave(values)) {
        return;
      }

      const productCategories = values?.productType?.id ? [values?.productType?.id] : [];
      const data = {
        id: Number(id),
        type: values?.type,
        code: values?.zipCode,
        startDate: values.startDate ? dateFormatForApi(values.startDate) : null,
        endDate: values.endDate ? dateFormatForApi(values.endDate) : null,
        uninstallDate: values?.uninstallDate ? dateFormatForApi(values.uninstallDate) : null,
        name: values.name,
        postingLocation: getPostingLocationForSubmission(
          {
            productType: values?.productType,
            name: values.name,
            postingLocation: values.postingLocation,
          },
          productTypes,
        ),
        address1: values.address1,
        address2: values.address2,
        phone1: getPhNo10Digits(values.phone1),
        phone2: getPhNo10Digits(values.phone2),
        openHours: values.openHours ? get24HoursTimeFormat(values.openHours) : null,
        closeHours: values.closeHours ? get24HoursTimeFormat(values.closeHours) : null,
        locationId: locationId,
        spaceIdentifier: values?.spaceIdentifier,
        productCategories: productCategories || [],
        areaTaxLocation: values?.areaTaxLocation?.value || null,
        areaTaxLocationId: values?.areaTaxLocation?.id || null,
        miscTax: values?.miscTax || '',
        creditCardFeeRate: values?.ccFeePercent === '' ? null : values?.ccFeePercent ?? null,
        pennyCostPercentage: values?.pennyCostPercentage || 0,
        locPaysTaxFlag: values?.locPaysTaxFlag,
        calculateRent: values?.calculateRent,
        taxExemptFlag: values?.taxExemptFlag,
        isCreditCardInCollection: values?.isCreditCardInCollection,
        feedSource: values?.feedSource?.length > 0 ? values.feedSource.map((item) => item.value) : null,
        creditCashControl: creditCashControlValue?.value || null,
      };
      updateSubLocationApi(data).then((res) => {
        if (res.data) {
          navigate(`/${routeConstants.SUBLOCATION_ROUTE}/${Number(id)}?locationId=${locationId}`);
        }
      });
    },
  });

  const isInactiveAreaTaxLocation = useMemo(
    () => isSelectedAreaTaxLocationInactive(formik.values, subLocationDetails, areaTaxLocations),
    [formik.values.areaTaxLocation, formik.values.areaTaxLocationId, subLocationDetails, areaTaxLocations],
  );
  const { touched, errors, getFieldProps, handleSubmit, values, setFieldValue } = formik;

  // Auto-populate CC Fee % with the lease's default rate when sublocation doesn't have a specific value
  useEffect(() => {
    if (subLocationDetails?.creditCardFeeRate == null && leaseCCFeeRate != null && values.ccFeePercent === '') {
      setFieldValue('ccFeePercent', leaseCCFeeRate);
    }
  }, [leaseCCFeeRate, subLocationDetails?.creditCardFeeRate, values.ccFeePercent, setFieldValue]);

  // Auto-populate postingLocation when name or productType changes (for non-Arcade/GameRoom)
  usePostingLocationSync(formik, productTypes);

  const standaloneChangeHandler = (e) => {
    const isStandalone = e.target.checked;
    setFieldValue('calculateRent', isStandalone);

    if (isStandalone) {
      setCreditCashControlValue(null);
      setFieldValue('creditCashControl', null);
    } else {
      setCreditCashControlValue(null);
      setFieldValue('creditCashControl', null);
    }
  };

  const autoCompleteChangeHandler = (name, value) => {
    setFieldValue(name, value);
    if (name === 'areaTaxLocation') {
      setAreaTaxLocationSearchText(value ? value.text : '');
      if (!value) {
        setFieldValue('areaTaxLocationId', null);
      }
    }
  };

  const onInputChangeHandler = (event, value) => {
    setAreaTaxLocationSearchText(value);
  };

  const startDateChangeHandler = (value) => {
    setFieldValue('startDate', value);
  };
  const endDateChangeHandler = (value) => {
    setFieldValue('endDate', value);
  };

  const uninstallDateChangeHandler = (value) => {
    setFieldValue('uninstallDate', value);
  };

  const onCancelClickHandler = () => {
    navigate(`/${routeConstants.SUBLOCATION_ROUTE}/${Number(id)}?locationId=${locationId}`);
  };

  const onArchiveClickHandler = () => {
    const status = subLocationDetails?.status;
    updateSubLocationApi({
      id: id,
      status: !status,
    }).then((res) => {
      if (res.error) {
        const errorMessage = res.error?.data?.message || MESSAGE.SUBLOCATION_STATUS_UPDATE_FAILED;
        openErrorModal(errorMessage);
        return;
      }

      if (res.data) {
        navigate(`/${routeConstants.SUBLOCATION_ROUTE}/${Number(id)}?locationId=${locationId}`);
      }
    });
  };

  const locPaysTaxFlagChangeHandler = (e) => {
    setFieldValue('locPaysTaxFlag', e.target.checked);
  };

  const isCreditCardInCollectionChangeHandler = (e) => {
    setFieldValue('isCreditCardInCollection', e.target.checked);
  };

  return (
    <Box component={Paper} p={3}>
      <Box mb={3} px={2}>
        <Typography variant="h2">Sub Location Details</Typography>
      </Box>
      <Box>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <GridContainer m={1} rowSpacing={3} columnSpacing={3} alignItems="center">
              <GridItem xs={12} md={6}>
                <TextField
                  label="Sub Location Name"
                  {...getFieldProps('name')}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  InputProps={{
                    'data-testid': 'editSubLocation',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Posting Location"
                  {...getFieldProps('postingLocation')}
                  error={Boolean(touched.postingLocation && errors.postingLocation)}
                  helperText={touched.postingLocation && errors.postingLocation}
                  disabled={!isArcadeOrGameRoomType(values.productType, productTypes)}
                  InputProps={{
                    'data-testid': 'editSubLocation',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Phone 1"
                  {...getFieldProps('phone1')}
                  InputProps={{
                    inputComponent: TextMaskCustom,
                    'data-testid': 'editSubLocation',
                  }}
                  error={Boolean(touched.phone1 && errors.phone1)}
                  helperText={touched.phone1 && errors.phone1}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <FieldLabel label="Location" value={values.location} dataTestId="editSubLocation" />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Phone 2"
                  {...getFieldProps('phone2')}
                  InputProps={{
                    inputComponent: TextMaskCustom,
                    'data-testid': 'editSubLocation',
                  }}
                  error={Boolean(touched.phone2 && errors.phone2)}
                  helperText={touched.phone2 && errors.phone2}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ width: '50%' }}>
                    <FieldLabel
                      label="Open Hours"
                      value={getDisplayTime(values.openHours)}
                      dataTestId="editSubLocation"
                    />
                  </Box>
                  <Box sx={{ width: '50%' }}>
                    <FieldLabel
                      label="Close Hours"
                      value={getDisplayTime(values.closeHours)}
                      dataTestId="editSubLocation"
                    />
                  </Box>
                </Stack>
              </GridItem>
              <GridItem xs={12} md={6}>
                <Select
                  inputLabel="Sub Location Type"
                  options={locationTypes}
                  {...getFieldProps('type')}
                  data-testid="editSubLocation"
                  error={formik.touched.type && formik.errors.type ? true : false}
                  helperText={formik.touched.type && formik.errors.type ? formik.errors.type : ''}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Datepicker
                  label="Installed Date"
                  name="startDate"
                  value={values.startDate}
                  onChange={startDateChangeHandler}
                  inputFormat="MM/DD/YYYY"
                  InputProps={{
                    'data-testid': 'editSubLocation',
                  }}
                  error={Boolean(formik.touched.startDate && formik.errors.startDate)}
                  helperText={formik.touched.startDate && formik.errors.startDate}
                />
                {!formik.values.startDate ? <FieldWarning field="Installed Date"></FieldWarning> : null}
              </GridItem>
              <GridItem xs={12} md={6}>
                <Datepicker
                  label="Lease End Date"
                  name="endDate"
                  value={values.endDate}
                  onChange={endDateChangeHandler}
                  inputFormat="MM/DD/YYYY"
                  InputProps={{
                    'data-testid': 'editSubLocation',
                  }}
                  error={Boolean(formik.touched.endDate && formik.errors.endDate)}
                  helperText={formik.touched.endDate && formik.errors.endDate}
                />
                {!formik.values.endDate ? <FieldWarning field="Lease End Date"></FieldWarning> : null}
              </GridItem>
              <GridItem xs={12} md={6}>
                <Datepicker
                  label="Uninstalled Date"
                  name="uninstallDate"
                  value={values.uninstallDate}
                  onChange={uninstallDateChangeHandler}
                  inputFormat="MM/DD/YYYY"
                  InputProps={{
                    'data-testid': 'editSubLocation',
                  }}
                  error={Boolean(formik.touched.uninstallDate && formik.errors.uninstallDate)}
                  helperText={formik.touched.uninstallDate && formik.errors.uninstallDate}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Space Identifier"
                  {...getFieldProps('spaceIdentifier')}
                  InputProps={{
                    'data-testid': 'editSubLocation',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Address 1"
                  {...getFieldProps('address1')}
                  multiline
                  error={Boolean(touched.address1 && errors.address1)}
                  helperText={touched.address1 && errors.address1}
                  InputProps={{
                    'data-testid': 'editSubLocation',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Address 2"
                  {...getFieldProps('address2')}
                  multiline
                  error={Boolean(touched.address2 && errors.address2)}
                  helperText={touched.address2 && errors.address2}
                  InputProps={{
                    'data-testid': 'editSubLocation',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Zipcode"
                  multiline
                  maxRows={3}
                  {...getFieldProps('zipCode')}
                  error={Boolean(touched.zipCode && errors.zipCode)}
                  helperText={touched.zipCode && errors.zipCode}
                  InputProps={{
                    'data-testid': 'editSubLocation',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <FieldLabel
                  label="Country"
                  value={countryOptions.find((option) => option.value === values.country)?.text}
                  dataTestId="editSubLocation"
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <FieldLabel
                  label="State"
                  value={stateOptions.find((option) => option.value === values.state)?.text || 'N/A'}
                  dataTestId="editSubLocation"
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <FieldLabel
                  label="City"
                  value={citiesOptions.find((option) => option.value === values.city)?.text || 'N/A'}
                  dataTestId="updateField"
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Autocomplete
                  label="Product Type"
                  data-testid="form-elements"
                  value={formik.values.productType || { text: '', value: '' }}
                  options={productTypeOptions || []}
                  getOptionLabel={(option) => option.text || ''}
                  onChange={(e, value) => {
                    formik.setFieldValue('productType', value);
                  }}
                  onBlur={() => formik.setFieldTouched('productType')}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  clearOnBlur={false}
                  error={Boolean(touched.productType && errors.productType)}
                  helperText={touched.productType && errors.productType}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <FieldLabel label="Vendor Code" value={values.vendorCode || 'N/A'} dataTestId="form-elements" />
              </GridItem>
              <GridItem xs={12} md={6}>
                <FieldLabel label="Vendor Name" value={values.vendorName || 'N/A'} dataTestId="form-elements" />
              </GridItem>
              <GridItem xs={12} md={6}>
                <FieldLabel
                  label="State tax"
                  value={
                    values.stateTaxRate && String(values.stateTaxRate).trim() !== ''
                      ? `${formatTaxPercentValue(values.stateTaxRate)}`
                      : undefined
                  }
                  dataTestId="updateField"
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <FieldLabel
                  label="Area tax"
                  value={
                    values.areaTaxRate && String(values.areaTaxRate).trim() !== ''
                      ? `${formatTaxPercentValue(values.areaTaxRate)}`
                      : undefined
                  }
                  dataTestId="updateField"
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <PercentTextField
                  label="Misc tax"
                  useFormik={true}
                  allowDecimal
                  {...getFieldProps('miscTax')}
                  InputProps={{
                    'data-testid': 'updateField',
                  }}
                />
                {!formik.values.miscTax ? <FieldWarning field="Misc Tax"></FieldWarning> : null}
              </GridItem>
              <GridItem xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Tooltip
                    title={
                      subLocationDetails?.creditCardFeeRate != null
                        ? 'overridden from Sublocation edit'
                        : 'Lease Default CC Fee'
                    }
                    arrow
                  >
                    <Chip
                      label={subLocationDetails?.creditCardFeeRate != null ? 'Custom CC fee' : 'Lease default CC fee'}
                      size="small"
                      color={subLocationDetails?.creditCardFeeRate != null ? 'primary' : 'default'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Tooltip>
                </Box>
                <PercentTextField
                  label="Credit Card Fee %"
                  useFormik={true}
                  allowDecimal
                  allowZero={true}
                  {...getFieldProps('ccFeePercent')}
                  InputProps={{
                    'data-testid': 'updateField',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <PercentTextField
                  label="Penny Cost %"
                  useFormik={true}
                  allowDecimal
                  allowZero={true}
                  decimalPlaces={4}
                  {...getFieldProps('pennyCostPercentage')}
                  InputProps={{
                    'data-testid': 'updateField',
                  }}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Select
                  options={taxExemptOptions}
                  inputLabel="Tax Exempt"
                  {...getFieldProps('taxExemptFlag')}
                  data-testid="updateField"
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Autocomplete
                  label="Area Tax Location"
                  value={formik.values.areaTaxLocation || null}
                  options={areaTaxLocations || []}
                  getOptionLabel={(option) => option.text || ''}
                  onChange={(e, value) => autoCompleteChangeHandler('areaTaxLocation', value)}
                  onBlur={() => formik.setFieldTouched('areaTaxLocation', true)}
                  onInputChange={onInputChangeHandler}
                  isOptionEqualToValue={(option, value) => option.id === value.id || option.value === value.value}
                  error={isInactiveAreaTaxLocation}
                  data-testid="editSubLocation"
                />
                {!formik.values.areaTaxLocation?.value ? (
                  <FieldWarning field="Area Tax Location"></FieldWarning>
                ) : isInactiveAreaTaxLocation ? (
                  <FormHelperText error>{MESSAGE.INACTIVE_AREA_TAX_SAVE_ERROR}</FormHelperText>
                ) : null}
              </GridItem>
              <GridItem xs={12} md={4}>
                <CheckBox
                  label="Location Pays Tax"
                  labelPlacement="end"
                  checked={values.locPaysTaxFlag}
                  onChange={locPaysTaxFlagChangeHandler}
                  data-testid="editSubLocation"
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <CheckBox
                  label="Standalone"
                  labelPlacement="end"
                  checked={values.calculateRent}
                  onChange={standaloneChangeHandler}
                  data-testid="editSubLocation"
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <CheckBox
                  label="Credit Card In Collection"
                  labelPlacement="end"
                  checked={values.isCreditCardInCollection}
                  onChange={isCreditCardInCollectionChangeHandler}
                  data-testid="editSubLocation"
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <Autocomplete
                  key={`creditCashControl-${values.calculateRent}-${creditCashControlValue?.value || 'null'}`}
                  label="Control Credit and Cash"
                  value={creditCashControlValue || null}
                  options={getCreditCashOptions(values.calculateRent)}
                  getOptionLabel={(option) => option.text || ''}
                  onChange={(e, value) => {
                    setCreditCashControlValue(value);
                    setFieldValue('creditCashControl', value);
                  }}
                  onBlur={() => formik.setFieldTouched('creditCashControl')}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  clearOnBlur={false}
                  required={values.calculateRent}
                  error={Boolean(touched.creditCashControl && errors.creditCashControl)}
                  helperText={touched.creditCashControl && errors.creditCashControl}
                />
              </GridItem>
              <GridItem xs={12} md={12}>
                <Autocomplete
                  label="Payment source"
                  data-testid="form-elements"
                  value={formik.values.feedSource || []}
                  options={FEED_SOURCE_OPTIONS}
                  getOptionLabel={(option) => option.text || ''}
                  onChange={(e, value) => {
                    formik.setFieldValue('feedSource', value);
                  }}
                  onBlur={() => formik.setFieldTouched('feedSource')}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  clearOnBlur={false}
                  multiple
                  limitTags={3}
                  disableCloseOnSelect
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.text}
                        size="small"
                        {...getTagProps({ index })}
                        key={option.value}
                      />
                    ))
                  }
                  error={Boolean(touched.feedSource && errors.feedSource)}
                  helperText={touched.feedSource && errors.feedSource}
                />
              </GridItem>
            </GridContainer>
            <GridContainer justifyContent="space-around" m={1} spacing={2}>
              <GridItem xs={6}>
                <LoadingButton
                  loading={updating}
                  variant="contained"
                  type="submit"
                  disabled={isInactiveAreaTaxLocation}
                >
                  UPDATE
                </LoadingButton>
                <Button onClick={onCancelClickHandler}>CANCEL</Button>
              </GridItem>
              <GridItem xs={6} display="flex" justifyContent="flex-end">
                <Button
                  onClick={onStatusToggleClickHandler}
                  color={subLocationDetails?.status ? 'error' : 'success'}
                  disabled={!subLocationDetails?.status && isInactiveAreaTaxLocation}
                >
                  {subLocationDetails?.status ? 'Archive' : 'Activate'} sub location
                </Button>
              </GridItem>
            </GridContainer>
            <Modal
              title={MESSAGE.ARCHIVE_SUBLOCATION__WARNING_TITLE}
              titleVariant="subtitle10"
              open={showWarningModal}
              onClose={() => setShowWarningModal(false)}
              actionButtons={
                <Box my={3}>
                  <Button
                    size="large"
                    onClick={() => {
                      setShowWarningModal(false);
                    }}
                    variant="contained"
                  >
                    {MESSAGE.OK}
                  </Button>
                </Box>
              }
            >
              <Box>
                <DialogContentText variant="h2">{MESSAGE.ARCHIVE_SUBLOCATION_WARNING_DESCRIPTION}</DialogContentText>
              </Box>
            </Modal>
            <InactiveAreaTaxModal open={showInactiveAreaTaxModal} onClose={() => setShowInactiveAreaTaxModal(false)} />
            <ConfirmationModal
              isLoading={updating}
              isOpen={showArchiveModal}
              title="Confirmation"
              msg={`Are you sure you want to ${
                subLocationDetails?.status ? 'archive' : 'active'
              } this sub location? This process cannot be undone.`}
              buttons={[
                { text: subLocationDetails?.status ? 'Archive' : 'Active', value: true },
                { text: 'Cancel', value: false },
              ]}
              onClick={(value) => {
                if (value) onArchiveClickHandler();
                else setShowArchiveModal(false);
              }}
            />
            <ErrorModal open={errorModal.open} onClose={closeErrorModal} message={errorModal.message} />
          </Form>
        </FormikProvider>
      </Box>
    </Box>
  );
};

export default SubLocationUpdateView;
